<template>
  <div class="share-dialog d-flex flex-column">
    <div class="d-flex flex-row align-items-center mb-2" v-if="title">
      <img v-if="iconUrl" class="mr-1" height="25px" width="25px" :src="iconUrl" alt="" />
      <h5 class="m-0 text-truncate">{{ title }}</h5>
      <span class="flex-fill"/>
      <b-btn v-if="isInModal" class="ml-1 text-muted" @click="() => $emit('modal-close')" size="sm" variant="transparent">
        <font-awesome-icon icon="times"/>
      </b-btn>
    </div>

    <slot name="text"/>

    <div class="share-icons my-2 d-flex flex-row align-items-center flex-wrap"
      :class="{ 'justify-content-start': !centered, 'justify-content-center': centered }">
        <b-btn v-for="(provider, i) in supportedProviderList" :key="`${provider}-${i}`"
          v-bind="buildProviderProps(provider)" @click="() => trackShareUrlClick(provider)">
            <font-awesome-icon v-if="provider === 'email'" icon="envelope" size="lg"/>
            <font-awesome-icon v-else-if="provider === 'linkedin'" :icon="['fab', 'linkedin']" size="lg"/>
            <font-awesome-icon v-else-if="provider === 'xing'" :icon="['fab', 'xing']" size="lg"/>
            <font-awesome-icon v-else-if="provider === 'facebook'" :icon="['fab', 'facebook']" size="lg"/>
            <font-awesome-icon v-else-if="provider === 'fb-messenger'" :icon="['fab', 'facebook-messenger']" size="lg"/>
            <font-awesome-icon v-else-if="provider === 'whatsapp'" :icon="['fab', 'whatsapp']" size="lg"/>
            <font-awesome-icon v-else-if="provider === 'twitter'" :icon="['fab', 'twitter']" size="lg"/>
        </b-btn>

        <b-btn v-if="canNativeShare" class="text-dark" @click="triggerNativeShare" variant="light">
          <!-- <span class="d-inline-block d-sm-none">{{ $t('ctas.more-options') }}</span> -->
          <font-awesome-icon icon="share"/>
        </b-btn>

        <div class="mt-2 my-1 w-100"/>
        <p class="m-0">{{ $t('campaign.search-linkedin.code') }}</p>
        <b-btn class="d-block text-truncate text-left px-0" disabled :href="url" variant="transparent">{{ url }}</b-btn>
        <b-btn class="text-dark" :disabled="!url" v-clipboard:copy="url" v-clipboard:success="() => trackShareUrlClick('url')" variant="light">
          {{ $t('share.ctas.copy-link') }} <font-awesome-icon icon="link"/>
        </b-btn>
    </div>

    <div class="my-2" v-if="exampleShareText">
      <template>
        <p class="m-0">{{ $t('share.sample-message.unsure-suggestion') }}</p>
        <blockquote class="share-example lead text-muted my-2" v-html="exampleShareText" />
        <div class="d-flex flex-row" :class="{ 'justify-content-start': !centered, 'justify-content-center': centered }">
          <a href="#" @click="trackExampleTextClick" v-clipboard:copy="`${exampleShareText}\n${url}`">
            <small>{{ $t('share.ctas.copy-sample-message') }}</small>
          </a>
          <!-- <span class="flex-fill"/> -->
          <!-- <a href="#"><small>Refresh</small></a> -->
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import assert from 'http-assert-plus';
import Vue from 'vue';
// import ls from 'local-storage';
import gridMixin from '@src/mixins/grid';
// import parseDate from 'date-fns/parse';
import { getShareURL } from '@/lib/share';
import { mapRoot, mapPreferences } from '@src/mixins/root';
import { hasTimePassed } from '@/lib/utils';

export const defaultShareProviders = [ 'email', 'linkedin', 'whatsapp', 'facebook', 'fb-messenger', 'twitter' ];
export const supportedProviders = [ 'email', 'linkedin', 'whatsapp', 'facebook', 'fb-messenger', 'twitter', 'xing' ];

export async function triggerNativeShare(url, trackFn) {
  if (process.env.VUE_ENV === 'client') {
    if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'SHARE', url: this.url }));
    } else {
      try {
        await window.navigator.share({ url: this.url });
      } catch (err) {
        if (err.name === 'NotAllowedError' && err.message.startsWith('The request is not allowed')) {
          window.location.reload();
        } else {
          throw err;
        }
      }
    }
  }

  typeof trackFn === 'function' ? await trackFn() : undefined; // eslint-disable-line no-unused-expressions
}

export default {
  props: {
    url: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: null,
    },
    providers: {
      type: Array,
      default: () => defaultShareProviders,
    },
    source: {
      type: String,
      default: 'job'
    },
    centered: {
      type: Boolean,
      default: () => false,
    },
    previewText: {
      type: String,
      default: null,
    },
    previewArgs: {
      type: Object,
      default: () => null,
    },
    postType: {
      type: String,
      default: 'link'
    },
  },
  mixins: [ gridMixin ],
  data: () => ({
    exampleShareClicked: false,
    togglePreview: false,
  }),
  mounted() {
    // Initialize Facebook SDK
    window.fbAsyncInit = function () {
      // eslint-disable-next-line no-undef
      window.FB.init({
        appId: 900214185188803,
        cookie: true, // Enable cookies to allow the server to access the session
        xfbml: true, // Parse social plugins on this webpage
        version: 'v21.0'
      });
    };
    // Load the SDK
    (function (d, s, id) {
      // eslint-disable-next-line one-var, prefer-const
      let js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) { return; }
      // eslint-disable-next-line prefer-const
      js = d.createElement(s); js.id = id;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));

    if (process.env.VUE_ENV === 'client' && !this.hasTouchscreen) {
      Array.prototype.forEach.call(this.$el.querySelectorAll('a.share-new-window[href][target="_blank"]'), el => {
        const { href } = el;
        el.addEventListener('click', function clickHandler(event) {
          event.preventDefault(); // Prevent the link from opening as usual
          try {
            const opened = window.open(href, '', 'width=600,height=600');
            assert(opened, new Error(`Failed to open ${href} in a new window`));
          } catch (err) {
            Vue.$log.error(err);
            // Remove the listener so the link opens as usual on next-click
            el.removeEventListener('click', clickHandler);
          }
        });
      });
    }
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'isNativeApp', 'isAssurant', 'isHelloFresh' ]),
    ...mapPreferences([ 'enableBrowserNativeShare', 'precacheLinkedInPreview', 'delayShareButtonClick', 'featuresEnabled' ]),
    hasContentSharesV2Enabled() {
      return (this.featuresEnabled || []).includes('content-share-v2');
    },
    hasJobShareV2Enabled() {
      return (this.featuresEnabled || []).includes('job-share-v2');
    },
    hasFacebookEnabled() {
      return (this.featuresEnabled || []).includes('facebook-share-v2');
    },
    iconUrl() {
      const { siteBranding: branding } = this.company || {};
      return branding && branding.smallLogo ? branding.smallLogo.fileLocation : null;
    },
    isInModal() {
      return this.$listeners && typeof this.$listeners['modal-close'] === 'function';
    },
    preferences() {
      const { sharePreferences = {} } = this.$root.company;
      return sharePreferences;
    },
    supportedProviderList() {
      return this.providers.filter(name => supportedProviders.includes(name));
    },
    canNativeShare() {
      if (process.env.VUE_ENV === 'client') {
        if (this.isNativeApp) {
          return window && window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function';
        } else if (this.enableBrowserNativeShare) {
          return window && window.navigator && typeof window.navigator.share === 'function';
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
    exampleShareText() {
      let text = this.previewText;

      if (this.isAssurant && this.user && this.user.locale === 'fr') {
        text = 'Assurant recrute ! Nous recherchons un(e) {jobTitle} pour rejoindre notre équipe. Consultez l’annonce et postulez maintenant !';
      }

      if (this.isHelloFresh && this.user && this.user.locale === 'de') {
        text = 'Hast du Lust mit mir bei HelloFresh als {jobTitle} zu arbeiten? Jetzt bewerben!';
      }

      return typeof text === 'string'
        ? text.trim().replace(/\{([\w\d_]+)\}/g, (value, key) => {
          if (this.previewArgs && this.previewArgs[key]) {
            return this.previewArgs[key];
          } else {
            return value;
          }
        })
        : null;
    },
    // @link https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
    hasTouchscreen() {
      let hasTouchscreen = false;
      if ('maxTouchPoints' in navigator) {
        hasTouchscreen = navigator.maxTouchPoints > 0;
      } else if ('msMaxTouchPoints' in navigator) {
        hasTouchscreen = navigator.msMaxTouchPoints > 0;
      } else {
        const mQ = window.matchMedia && matchMedia('(pointer:coarse)');
        if (mQ && mQ.media === '(pointer:coarse)') {
          hasTouchscreen = !!mQ.matches;
        } else if ('orientation' in window) {
          hasTouchscreen = true; // deprecated, but good fallback
        } else {
        // Only as a last resort, fall back to user agent sniffing
          const UA = navigator.userAgent;
          hasTouchscreen = (
            /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
            /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
          );
        }
      }
      return hasTouchscreen;
    },
  },
  methods: {
    buildProviderProps(provider) {
      let href = null;
      try {
        const { company, exampleShareText: shareText } = this;
        href = getShareURL(this.$i18n, { channel: provider, company, shareText, shareUrl: this.url });
      } catch (err) {
        this.$log.error(err);
        href = this.url;
      }

      if (this.precacheLinkedInPreview && provider === 'linkedin') {
        // make a request to https://www.linkedin.com/post-inspector/inspect/${url} to prep the cache
        // discard the response, we don't care
        fetch(`https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(href)}`, { mode: 'no-cors' });
      }

      if (provider === 'facebook' && this.hasFacebookEnabled) {
        href = undefined;
      } else {
        if (provider === 'linkedin' && ((this.hasJobShareV2Enabled && this.source === 'job') || (this.hasContentSharesV2Enabled && this.source === 'content'))) {
          href = undefined;
        }
      }

      return {
        class: provider === 'email' ? '' : 'share-new-window',
        href,
        target: '_blank',
        variant: provider,
      };
    },
    getChannelShareUrl(channel) {
      try {
        const { company, exampleShareText: shareText } = this;
        return getShareURL(this.$i18n, { channel, company, shareText, shareUrl: this.url });
      } catch (err) {
        this.$log.error(err);
        return this.url;
      }
    },
    trackShareUrlClick(channel) {
      const { state } = this.user || {};
      const { linkedIn, facebook } = state || {};
      const { accessToken: liToken, expiresAt, version } = linkedIn || {};
      const { loginAccessExpiresIn, updatedTime } = facebook || {};

      const now = new Date().getTime();
      const isLinkedInConnected = liToken && expiresAt && expiresAt > now && version === 'v1';
      const isFacebookConnected = updatedTime && loginAccessExpiresIn && !hasTimePassed(updatedTime, loginAccessExpiresIn);

      const shareUrl = this.url.includes('localhost') ? this.url.replace('http://localhost:8080', 'https://referrals.reallinks.dev/') : this.url; // urls from localhost aren't displayed

      if (channel === 'facebook' && this.hasFacebookEnabled) {
        if (isFacebookConnected) {
          window.FB.ui(
            {
              method: 'share',
              display: 'popup',
              href: shareUrl
            }
          );
        } else {
          this.$emit('openFacebookConnectionModal');
        }
      } else if (channel === 'linkedin' && this.hasContentSharesV2Enabled && this.source === 'content') {
        if (isLinkedInConnected) {
          this.$emit('openLinkedInPostModal');
        } else {
          this.$emit('openLinkedInConnectionModal');
        }
      } else if (channel === 'linkedin' && this.hasJobShareV2Enabled && this.source === 'job') {
        if (isLinkedInConnected) {
          this.$emit('openLinkedInJobsModal');
        } else {
          this.$emit('openLinkedInJobConnectionModal');
        }
      } else if (this.delayShareButtonClick && this.source !== 'content') {
        const { href: link, target } = this.buildProviderProps(channel);
        this.$emit('track', { channel });

        setTimeout(() => {
          const a = document.createElement('a');
          a.target = target;
          a.href = link;
          a.click();
        }, 1000);
      } else {
        this.$emit('track', { channel });
      }
    },
    trackExampleTextClick() {
      if (!this.exampleShareClicked) {
        this.exampleShareClicked = true;
        this.$emit('track', { channel: 'example-message' });
      }
    },
    async triggerNativeShare() {
      if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'SHARE', url: this.url }));
      } else {
        try {
          await window.navigator.share({ url: this.url });
        } catch (err) {
          if (err.name === 'NotAllowedError' && err.message.startsWith('The request is not allowed')) {
            window.location.reload();
          } else {
            throw err;
          }
        }
      }
      this.$emit('track', { channel: 'native' });
    },
  },
};
</script>

<style lang="scss">
.share-dialog {

  .form-control-disabled {
    background-color: #e9ecef;
    opacity: 1;
    user-select: none;
    word-break: break-all;
    white-space: nowrap;
  }

  .share-icons {
    margin-left: -3px;
    margin-right: -3px;

    .btn {
      margin: 1px;
      color: white;

      &.btn-email {
        background-color: #3b987b;
      }
      &.btn-linkedin {
        background-color: #0e76a8;
      }
      &.btn-whatsapp {
        background-color: #00ab00;
      }
      &.btn-facebook {
        background-color: #3b5998;
      }
      &.btn-fb-messenger {
        background-color: #0078FF;
      }
      &.btn-twitter {
        background-color: #55acee;
      }
      &.btn-xing {
        background-color: #1A7576;
      }
    }
  }

  .share-example {
    border-left: 3px solid #eee;
    padding-left: 7px;
  }

}
</style>



// WEBPACK FOOTER //
// src/components/Share.vue